import React, { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { X, Undo2, History, ChevronDown, ChevronUp } from 'lucide-react';
import { formatSignedValueForDisplay } from './DbqElements';
import { getMusculoskeletalSupplementalFieldDefinition } from './dbqMusculoskeletalConstants';

// QA "Changes since signing" panel (2026-08-04): the single surface for
// post-sign edit visibility now that per-field badges are gone. Lists every
// stamped field grouped by section - prior (signed) value vs current, who and
// when - with click-to-scroll and a per-row Swap back. Rendered only for
// claims:approve_for_va holders (the editor gates it); `onSwapBack` is null
// when the request is finalized (view-only history).

const MSK_PREFIX = '_supplemental.musculoskeletal';
const MSK_SECTION_LABEL = 'Musculoskeletal supplemental';

// Resolve a stamped fieldKey to { label, sectionLabel, field } using the form
// template. Handles three key shapes:
//  - plain field ids ("dx_icd_code")
//  - repeating-group instance keys ("group_instance_<n>_<ts>__fieldId")
//  - MSK supplemental dotted paths ("_supplemental.musculoskeletal.remarks")
const buildFieldIndex = (formJson) => {
  const index = new Map();
  const walk = (elements, sectionLabel) => {
    (elements || []).forEach((element) => {
      if (element?.type === 'field' && element.id) {
        index.set(element.id, {
          label: element.label || element.id,
          sectionLabel,
          field: element,
        });
      }
      if (element?.elements) walk(element.elements, sectionLabel);
      // Option child elements (e.g. per-diagnosis ICD code / date fields
      // under a multi_select option) live under options[].child_elements.
      (element?.options || []).forEach((option) => {
        if (option?.child_elements) walk(option.child_elements, sectionLabel);
      });
    });
  };
  (formJson?.sections || []).forEach((section) => {
    walk(section.elements, section.label || section.id || 'Form');
  });
  return index;
};

const resolveFieldMeta = (fieldKey, fieldIndex) => {
  if (fieldKey.startsWith(`${MSK_PREFIX}.`)) {
    const suffix = fieldKey.slice(MSK_PREFIX.length + 1);
    const definition = getMusculoskeletalSupplementalFieldDefinition(fieldKey);
    return {
      label: definition?.label || suffix,
      sectionLabel: MSK_SECTION_LABEL,
      field: definition || null,
    };
  }
  const direct = fieldIndex.get(fieldKey);
  if (direct) return direct;
  // Repeating-group instance key: "<groupId>_instance_<n>_<ts>__<fieldId>".
  const instanceSplit = fieldKey.split('__');
  if (instanceSplit.length > 1) {
    const bareId = instanceSplit[instanceSplit.length - 1];
    const parent = fieldIndex.get(bareId);
    if (parent) {
      return { ...parent, label: `${parent.label} (entry)` };
    }
  }
  return { label: fieldKey, sectionLabel: 'Other', field: null };
};

// Current value lookup mirrors the key shapes above.
const getCurrentValue = (formData, fieldKey) => {
  if (!fieldKey.includes('.')) return formData?.[fieldKey];
  return fieldKey
    .split('.')
    .reduce(
      (cursor, segment) =>
        cursor && typeof cursor === 'object' ? cursor[segment] : undefined,
      formData,
    );
};

// Client mirror of the server's isSameSignedValue (dbqPostSignLock.js):
// undefined/null/'' all mean "empty"; non-strings compare structurally. Rows
// whose CURRENT value matches the signed value again (a just-clicked Swap
// back, a manual retype) disappear immediately instead of waiting for the
// autosave to round-trip the cleared stamp - the server converges to the
// same answer on the next save.
const isSameAsSignedValue = (a, b) => {
  const norm = (v) => (v === undefined || v === null ? '' : v);
  const left = norm(a);
  const right = norm(b);
  if (typeof left === 'string' && typeof right === 'string')
    return left === right;
  return JSON.stringify(left) === JSON.stringify(right);
};

// Live count of fields that CURRENTLY differ from what the provider signed.
// Exported so the banner's "Changes (N)" button agrees with the panel.
export const countLiveChanges = (postSignEdits, formData) =>
  Object.entries(postSignEdits || {}).filter(
    ([fieldKey, stamp]) =>
      !isSameAsSignedValue(
        getCurrentValue(formData, fieldKey),
        stamp?.signedValue,
      ),
  ).length;

// Word-level edge diff for long text: trims the common prefix and suffix and
// returns the changed middle with a few words of context on each side. For
// the typical QA edit (a tweak somewhere inside a long opinion), this
// pinpoints exactly what changed without an expensive full diff.
const CONTEXT_WORDS = 10;
const diffTextEdges = (priorText, currentText) => {
  const a = String(priorText || '').split(/(\s+)/);
  const b = String(currentText || '').split(/(\s+)/);
  let prefix = 0;
  while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) {
    prefix += 1;
  }
  let suffix = 0;
  while (
    suffix < a.length - prefix &&
    suffix < b.length - prefix &&
    a[a.length - 1 - suffix] === b[b.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const removed = a.slice(prefix, a.length - suffix).join('');
  const added = b.slice(prefix, b.length - suffix).join('');
  const contextBefore = a
    .slice(Math.max(0, prefix - CONTEXT_WORDS * 2), prefix)
    .join('');
  const contextAfter = a
    .slice(a.length - suffix, a.length - suffix + CONTEXT_WORDS * 2)
    .join('');
  return {
    removed,
    added,
    contextBefore,
    contextAfter,
    hasLeadingEllipsis: prefix > CONTEXT_WORDS * 2,
    hasTrailingEllipsis: suffix > CONTEXT_WORDS * 2,
  };
};

const isLongTextValue = (value) =>
  typeof value === 'string' && value.length > 140;

const FullTextBlock = ({ title, text, tone }) => (
  <div className="mb-2">
    <div
      className="text-uppercase fw-semibold"
      style={{ fontSize: 10, letterSpacing: 0.5, color: '#94a3b8' }}
    >
      {title}
    </div>
    <div
      className="small rounded border p-2 mt-1"
      style={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: 180,
        overflow: 'auto',
        background: tone === 'prior' ? '#fff7ed' : '#f0fdf4',
        borderColor: tone === 'prior' ? '#fed7aa' : '#bbf7d0',
      }}
    >
      {text === '' || text == null ? <em>(empty)</em> : text}
    </div>
  </div>
);

// Expanded view for a long-text change: the pinpointed removed/added middle
// with context, plus optional full prior/current texts.
const LongTextDiff = ({ prior, current }) => {
  const [showFull, setShowFull] = useState(false);
  const delta = useMemo(
    () => diffTextEdges(prior, current),
    [prior, current],
  );
  return (
    <div
      className="mt-2 rounded border p-2"
      style={{ background: '#ffffff', borderColor: '#e2e8f0' }}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="text-uppercase fw-semibold mb-1"
        style={{ fontSize: 10, letterSpacing: 0.5, color: '#94a3b8' }}
      >
        What changed
      </div>
      <div
        className="small"
        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {delta.hasLeadingEllipsis ? '… ' : ''}
        <span style={{ color: '#64748b' }}>{delta.contextBefore}</span>
        {delta.removed ? (
          <span
            style={{
              background: '#fee2e2',
              color: '#b91c1c',
              textDecoration: 'line-through',
              borderRadius: 3,
            }}
          >
            {delta.removed}
          </span>
        ) : null}
        {delta.added ? (
          <span
            style={{ background: '#dcfce7', color: '#166534', borderRadius: 3 }}
          >
            {delta.added}
          </span>
        ) : null}
        {!delta.removed && !delta.added ? (
          <em style={{ color: '#94a3b8' }}>(only whitespace differs)</em>
        ) : null}
        <span style={{ color: '#64748b' }}>{delta.contextAfter}</span>
        {delta.hasTrailingEllipsis ? ' …' : ''}
      </div>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 mt-1"
        style={{ fontSize: 11 }}
        onClick={() => setShowFull((open) => !open)}
      >
        {showFull ? 'Hide full text' : 'Show full text'}
      </button>
      {showFull && (
        <div className="mt-2">
          <FullTextBlock
            title="As signed"
            text={prior}
            tone="prior"
          />
          <FullTextBlock title="Current" text={current} tone="current" />
        </div>
      )}
    </div>
  );
};

const ValueLine = ({ tone, children }) => (
  <div
    className="small"
    style={{
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      color: tone === 'prior' ? '#94a3b8' : '#0f172a',
      textDecoration: tone === 'prior' ? 'line-through' : 'none',
      textDecorationColor: 'rgba(148, 163, 184, 0.6)',
    }}
  >
    {children}
  </div>
);

const DbqChangesPanel = ({
  postSignEdits,
  formData,
  formJson,
  onJumpToField,
  onSwapBack = null,
  // True when the tab is STALE (someone else saved; this tab's saves are
  // paused). The list may not reflect the latest server state and swap-backs
  // are disabled - the notice tells the reviewer to refresh first.
  staleNotice = false,
  // Visibility test for a field key. Rows for currently-HIDDEN fields (a
  // controlling answer hides them) are held back with a footer count: they
  // cannot be scrolled to or meaningfully restored until their controller is
  // restored first, so offering Swap back would just confuse (Nic,
  // 2026-08-04 - restore-order guarantee).
  isFieldVisible = null,
  onClose,
}) => {
  const fieldIndex = useMemo(() => buildFieldIndex(formJson), [formJson]);
  // Long-text rows can expand to a pinpointed diff (+ full texts).
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggleExpanded = (fieldKey) =>
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(fieldKey)) next.delete(fieldKey);
      else next.add(fieldKey);
      return next;
    });

  const { grouped, hiddenCount } = useMemo(() => {
    const all = Object.entries(postSignEdits || {})
      .map(([fieldKey, stamp]) => {
        const meta = resolveFieldMeta(fieldKey, fieldIndex);
        return {
          fieldKey,
          stamp,
          ...meta,
          currentValue: getCurrentValue(formData, fieldKey),
        };
      })
      // Already-restored fields drop out immediately (stamp clears server-side
      // on the next save; this is the live mirror of that rule).
      .filter(
        (entry) =>
          !isSameAsSignedValue(entry.currentValue, entry.stamp?.signedValue),
      );
    const visible =
      typeof isFieldVisible === 'function'
        ? all.filter((entry) => isFieldVisible(entry.fieldKey))
        : all;
    const entries = visible;
    const hidden = all.length - visible.length;
    const bySection = new Map();
    entries.forEach((entry) => {
      const key = entry.sectionLabel;
      if (!bySection.has(key)) bySection.set(key, []);
      bySection.get(key).push(entry);
    });
    bySection.forEach((rows) =>
      rows.sort(
        (a, b) =>
          new Date(b.stamp?.editedAt || 0) - new Date(a.stamp?.editedAt || 0),
      ),
    );
    return {
      grouped: Array.from(bySection.entries()),
      hiddenCount: hidden,
    };
  }, [postSignEdits, fieldIndex, formData, isFieldVisible]);

  const total = grouped.reduce((sum, [, rows]) => sum + rows.length, 0);

  return (
    <div
      className="shadow-lg"
      style={{
        position: 'fixed',
        top: 84,
        right: 16,
        bottom: 16,
        width: 380,
        maxWidth: 'calc(100vw - 32px)',
        zIndex: 1060,
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRadius: 14,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      <div
        className="d-flex align-items-center gap-2 px-3 py-2"
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          color: '#ffffff',
        }}
      >
        <History size={16} aria-hidden="true" />
        <div className="fw-semibold" style={{ fontSize: '0.9rem', flex: 1 }}>
          Changes since signing
          <span className="ms-2 fw-normal" style={{ opacity: 0.75 }}>
            {total} field{total === 1 ? '' : 's'}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close changes panel"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#ffffff',
            opacity: 0.8,
            lineHeight: 1,
            padding: 4,
          }}
        >
          <X size={17} />
        </button>
      </div>

      {staleNotice && (
        <div
          className="small px-3 py-2 fw-semibold"
          style={{
            background: '#fef2f2',
            color: '#b91c1c',
            borderBottom: '1px solid #fecaca',
          }}
        >
          This DBQ was updated elsewhere - this list may be out of date and
          swapping back is disabled. Refresh the page first.
        </div>
      )}

      <div className="flex-grow-1 overflow-auto px-3 py-2">
        {total === 0 ? (
          <div className="small py-3 text-center" style={{ color: '#64748b' }}>
            {hiddenCount > 0
              ? 'The remaining changes are on hidden fields - restore the controlling answer to reveal them.'
              : 'Every field matches what the provider signed.'}
          </div>
        ) : (
          grouped.map(([sectionLabel, rows]) => (
            <div key={sectionLabel} className="mb-3">
              <div
                className="text-uppercase fw-semibold mb-1"
                style={{ fontSize: 11, letterSpacing: 0.6, color: '#94a3b8' }}
              >
                {sectionLabel}
              </div>
              <div className="d-flex flex-column gap-2">
                {rows.map((row) => {
                  const prior = formatSignedValueForDisplay(
                    row.stamp?.signedValue,
                    row.field,
                  );
                  const current = formatSignedValueForDisplay(
                    row.currentValue,
                    row.field,
                  );
                  const isLongText =
                    isLongTextValue(prior) || isLongTextValue(current);
                  const isExpanded = expandedKeys.has(row.fieldKey);
                  return (
                    <div
                      key={row.fieldKey}
                      role="button"
                      tabIndex={0}
                      onClick={() => onJumpToField?.(row.fieldKey)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onJumpToField?.(row.fieldKey);
                        }
                      }}
                      className="rounded p-2"
                      style={{
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        cursor: 'pointer',
                      }}
                    >
                      <div className="d-flex align-items-start gap-2">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            className="fw-semibold"
                            style={{ fontSize: '0.82rem', color: '#1e293b' }}
                          >
                            {row.label}
                          </div>
                          <div className="mt-1">
                            <ValueLine tone="prior">
                              {prior === null ? (
                                <em>(no answer when signed)</em>
                              ) : (
                                prior
                              )}
                            </ValueLine>
                            <ValueLine tone="current">
                              {current === null ? <em>(cleared)</em> : current}
                            </ValueLine>
                          </div>
                          <div
                            className="mt-1"
                            style={{ fontSize: 11, color: '#94a3b8' }}
                          >
                            {row.stamp?.editorName || 'Someone'}
                            {row.stamp?.editedAt
                              ? ` · ${new Date(row.stamp.editedAt).toLocaleString()}`
                              : ''}
                            {Number(row.stamp?.count) > 1
                              ? ` · ${row.stamp.count} edits`
                              : ''}
                          </div>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1">
                          {typeof onSwapBack === 'function' && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="d-flex align-items-center gap-1 text-nowrap py-0 px-2"
                              style={{ fontSize: 11 }}
                              onClick={(event) => {
                                event.stopPropagation();
                                onSwapBack(
                                  row.fieldKey,
                                  row.stamp?.signedValue ?? '',
                                );
                              }}
                              title="Restore the value the provider signed"
                            >
                              <Undo2 size={12} />
                              Swap back
                            </Button>
                          )}
                          {isLongText && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 d-flex align-items-center gap-1 text-decoration-none"
                              style={{ fontSize: 11 }}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleExpanded(row.fieldKey);
                              }}
                              title="Show exactly what changed in this text"
                            >
                              {isExpanded ? (
                                <ChevronUp size={12} />
                              ) : (
                                <ChevronDown size={12} />
                              )}
                              {isExpanded ? 'Collapse' : 'View change'}
                            </button>
                          )}
                        </div>
                      </div>
                      {isLongText && isExpanded && (
                        <LongTextDiff
                          prior={
                            typeof prior === 'string' ? prior : (prior ?? '')
                          }
                          current={
                            typeof current === 'string'
                              ? current
                              : (current ?? '')
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        {total > 0 && hiddenCount > 0 && (
          <div
            className="small rounded px-2 py-2 mb-2"
            style={{
              background: '#f1f5f9',
              color: '#64748b',
              border: '1px dashed #cbd5e1',
            }}
          >
            {hiddenCount} more change{hiddenCount === 1 ? '' : 's'} on hidden
            field{hiddenCount === 1 ? '' : 's'} - restore the controlling
            answer above to reveal {hiddenCount === 1 ? 'it' : 'them'}.
          </div>
        )}
      </div>
    </div>
  );
};

export default DbqChangesPanel;
